import React from 'react';
import { Link } from 'react-router-dom';
import {
    convertFromWeiEthers,
    formatNumberWithCommas,
    formatSecondsToHms,
    getFormatedWeb3Address,
} from '../../utils/helpers';
import { ACTIVITY_TYPES, LIGHT_THEME } from '../../utils/constants';
import { useTheme } from '../../contexts/ThemeProvider';
import { useCountdownSeconds } from '../../hooks/useCountdownSeconds';
import LoaderTable from '../../hooks/loaderTable';
import CustomPagination from '../CustomPagination';

const SYSTEM_EVENT_ICONS = {
    [ACTIVITY_TYPES.DISPUTE_WINDOW]: {
        light: '/assets/warning.svg',
        dark: '/assets/warning.svg',
    },
    [ACTIVITY_TYPES.APPEAL_WINDOW]: {
        light: '/assets/detailpage/opendispute-icon.svg',
        dark: '/assets/detailpage/opendispute-icondark.svg',
    },
    [ACTIVITY_TYPES.MARKET_FINALIZED]: {
        light: '/assets/lightcheckmark.svg',
        dark: '/assets/darkcheckmark.svg',
    },
};

const BOLD_STYLE = { fontFamily: 'hbold', fontWeight: 700, color: 'var(--heading-text)' };
const LIQUIDITY_COLOR = '#12AAFF';
const ORDER_COLOR = '#8559E3';
const BUY_COLOR = '#29B207';

const Bold = ({ children, color }) => (
    <span style={{ ...BOLD_STYLE, color: color || BOLD_STYLE.color }}>{children}</span>
);

const getTimeAgo = (timestamp) => {
    const diffInMs = new Date() - new Date(timestamp);
    const diffInSecs = Math.floor(diffInMs / 1000);
    const diffInMins = Math.floor(diffInSecs / 60);
    const diffInHours = Math.floor(diffInMins / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInDays > 0) return `${diffInDays}d ago`;
    if (diffInHours > 0) return `${diffInHours}h ago`;
    if (diffInMins > 0) return `${diffInMins}m ago`;
    return `${diffInSecs}s ago`;
};

const WALLET_PATTERN = /^0x[a-fA-F0-9]{40}$/;

const getDisplayName = (user, fallbackWalletAddress) => {
    const username = user?.username;
    if (username) {
        return WALLET_PATTERN.test(username) ? getFormatedWeb3Address(username, true) : username;
    }
    const wallet = user?.walletAddress || fallbackWalletAddress;
    if (wallet) return getFormatedWeb3Address(wallet, true);
    return '';
};

const WindowCountdown = ({ endTime }) => {
    const secondsLeft = useCountdownSeconds(endTime);
    if (secondsLeft <= 0) return null;
    return <>. Time remaining: <Bold>{formatSecondsToHms(secondsLeft)}</Bold></>;
};

const ActivityDescription = ({ activity, poolsdetail, tokenToDisplay, tokenDecimals }) => {
    const name = getDisplayName(activity?.user, activity?.walletAddress);
    const token = tokenToDisplay;
    const type = activity?.type;
    const txType = activity?.transactionType;
    const choiceIndex = parseInt(activity.option);
    const optionName = poolsdetail?.options?.find((o) => o.choiceIndex === choiceIndex)?.optionName ?? activity.option;
    const shares = formatNumberWithCommas(activity.shares);

    if (txType === ACTIVITY_TYPES.ENTER_OPTION) {
        return (
            <>
                <Bold>{name}</Bold> bought{' '}
                <Bold>{shares} {optionName} {' '} shares</Bold>{' '}
                at <Bold color={BUY_COLOR}>{formatNumberWithCommas(activity.pricePerShare)} {token}</Bold>
            </>
        );
    }

    if (txType === ACTIVITY_TYPES.ENTER_LIQUIDITY || type === ACTIVITY_TYPES.ENTER_LIQUIDITY) {
        const amount = formatNumberWithCommas(convertFromWeiEthers(String(activity.amount), tokenDecimals));
        return (
            <>
                <Bold>{name}</Bold> added <Bold color={LIQUIDITY_COLOR}>{amount} {token}</Bold> liquidity to the market
            </>
        );
    }

    if (type === ACTIVITY_TYPES.CREATE_MARKET) {
        return (
            <>
                <Bold>{name}</Bold> created the market with{' '}
                <Bold color={LIQUIDITY_COLOR}>{formatNumberWithCommas(activity.amount)} {token}</Bold> initial liquidity
            </>
        );
    }

    if (type === ACTIVITY_TYPES.SUBMIT_ANSWER) {
        return (
            <>
                <Bold>{name}</Bold> submitted answer:{' '}
                <Bold>{activity.outcome}</Bold>{' '}
                for this market
            </>
        );
    }

    if (type === ACTIVITY_TYPES.TRIGGER_RESOLUTION) {
        return (
            <>
                <Bold>{name}</Bold> triggered resolution for this market with the answer:{' '}
                <Bold>{activity.outcome}</Bold>
            </>
        );
    }

    if (type === ACTIVITY_TYPES.MARKET_FINALIZED) {
        const answer = activity.outcome ?? activity.result;
        return (
            <>
                This market has been finalized. Result: <Bold>{answer}</Bold>
            </>
        );
    }

    if (type === ACTIVITY_TYPES.OPEN_DISPUTE) {
        return <><Bold>{name}</Bold> opened a dispute for this market</>;
    }

    if (type === ACTIVITY_TYPES.FILE_APPEAL) {
        return <><Bold>{name}</Bold> filed an appeal for this market</>;
    }

    if (type === ACTIVITY_TYPES.DISPUTE_WINDOW) {
        return <>Dispute window open<WindowCountdown endTime={activity.endTime} /></>;
    }

    if (type === ACTIVITY_TYPES.APPEAL_WINDOW) {
        return <>Appeal window open<WindowCountdown endTime={activity.endTime} /></>;
    }

    if (type === ACTIVITY_TYPES.RESOLUTION_REQUEST) {
        return (
            <>
                <Bold>{name}</Bold> requested resolution for this market
            </>
        );
    }
    if (txType === ACTIVITY_TYPES.EXIT_OPTION) {
        return (
            <>
                <Bold>{name}</Bold> offered{" "}
                <Bold>
                    {" "}
                    {shares} {optionName}
                </Bold>
                {" "}  shares for  <Bold color={ORDER_COLOR}>
                    {formatNumberWithCommas(activity.pricePerShare)} {token}
                </Bold>
            </>
        );
    }

    return <>{activity?.description}</>;
};

const ActivityRow = ({ activity, poolsdetail, tokenToDisplay, tokenDecimals, theme }) => {
    const isOlympusTrigger = activity?.type === ACTIVITY_TYPES.TRIGGER_RESOLUTION;
    const olympusLogo = theme === LIGHT_THEME ? '/assets/detailpage/olympusicon.svg' : '/assets/detailpage/olympusicon.svg';
    const systemIcon = SYSTEM_EVENT_ICONS[activity?.type]?.[theme];
    const imageSrc = systemIcon || activity?.user?.profilePicture || '/assets/detailpage/placeholder.svg';

    const row = (
        <div className="single-item">
            <div className="twice-imgtext updatedtwice-imgtext">
                {isOlympusTrigger ? (
                    <img
                        src={olympusLogo}
                        alt="Olympus"
                        className="olympus-trigger-logo"
                    />
                ) : (
                    <img src={imageSrc} alt="" className="img-fluid" />
                )}
                <h6>
                    {isOlympusTrigger && <Bold>Olympus </Bold>}
                    <ActivityDescription
                        activity={activity}
                        poolsdetail={poolsdetail}
                        tokenToDisplay={tokenToDisplay}
                        tokenDecimals={tokenDecimals}
                    />
                </h6>
            </div>
            <p>{getTimeAgo(activity.timestamp)}</p>
        </div>
    );

    if (!systemIcon && !isOlympusTrigger && activity?.user?._id) {
        return (
            <Link className="creator_link" to={`/user-profile?id=${activity.user._id}`}>
                {row}
            </Link>
        );
    }
    return row;
};

const ActivityTab = ({
    loadertable,
    poolactivity,
    poolsdetail,
    tokenToDisplay,
    tokenDecimals,
    currentPage,
    totalPages,
    handlePageChange,
}) => {
    const { theme } = useTheme();

    if (loadertable) {
        return (
            <div className="m-5">
                <LoaderTable />
            </div>
        );
    }

    return (
        <div className="activity-items">
            {poolactivity?.map((activity, index) => (
                <ActivityRow
                    key={index}
                    activity={activity}
                    poolsdetail={poolsdetail}
                    tokenToDisplay={tokenToDisplay}
                    tokenDecimals={tokenDecimals}
                    theme={theme}
                />
            ))}
            <CustomPagination
                currentPage={currentPage.activity}
                totalPages={totalPages.activity}
                onPageChange={(pageNumber) => handlePageChange('activity', pageNumber)}
            />
        </div>
    );
};

export default ActivityTab;